// R-2026-09-21: useLocalStorage 的 per-user 封装。
// 把 key 加 :userId 后缀,登出态读匿名 key(不持久化中间态)。
// 用途:UI 偏好(列可见性 / 折叠 / 列宽) — 跨账号不应继承。
//
// 关键差异 vs useLocalStorage:
// 1. scopedKey 依赖 userId,AuthProvider 加载完用户后会变化
//    → 必须 useEffect 监听 scopedKey 变化时重读 localStorage
//    (useState initializer 只在首次挂载跑一次,key 变了不会重读)
// 2. setter 支持函数式更新 (prev => next),原 useLocalStorage 不支持,
//    JSON.stringify(fn) 会变 undefined 写进去
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from './useAuth'

function readValue(key, defaultValue) {
  try {
    const raw = localStorage.getItem(key)
    return raw === null ? defaultValue : JSON.parse(raw)
  } catch {
    return defaultValue
  }
}

function writeValue(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch {}
}

export function useUserLocalStorage(key, defaultValue) {
  const { user } = useAuth()
  const userId = user?.id ?? null
  const scopedKey = userId ? `${key}:${userId}` : `${key}:_anon`

  const [value, setValue] = useState(() => readValue(scopedKey, defaultValue))

  // userId 从 null → 真实 id(AuthProvider 加载完)→ 重读对应 user 的 localStorage
  useEffect(() => {
    setValue(readValue(scopedKey, defaultValue))
  }, [scopedKey]) // defaultValue 故意不进依赖,避免每次 render 都重读

  const setValueAndSave = useCallback((newValue) => {
    setValue((current) => {
      const next = typeof newValue === 'function' ? newValue(current) : newValue
      // R-2026-10-23:user 未就绪(user=null 或 user.id 缺失)时不落盘。
      // 否则会写进共享的 ':_anon' key —— 所有"未登录 / 加载中"的会话共用同一份数据,
      // 表现为「换个账号看到别人的设置」。此时只保留内存态,登录后 effect 会按真实 uid 重读。
      if (userId) writeValue(scopedKey, next)
      return next
    })
  }, [scopedKey, userId])

  return [value, setValueAndSave]
}